import { useQuery } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { get } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { IntegrationCrmType } from "common/types";

import type { CsvState } from "../types";

type LeadsState = {
  state: "failed" | "in_progress" | "success";
  leads: Omit<CsvState, "fileName" | "mainIdentifier">;
};

async function getCrmLeadsStateRequest(
  workspaceId: string,
  crmType: IntegrationCrmType,
  sourceId: string,
): Promise<LeadsState> {
  const { state, leads } = await get(
    `workspaces/${workspaceId}/integrations/${crmType}/${crmType === "instantly" ? "lists" : "campaigns"}/${sourceId}/leads`,
  );

  return { state, leads };
}

export default function useCrmLeads(sourceId: string) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { id: campaignId, crm_type: crmType } = useCampaignContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);

  const { data: crmLeadsState, isLoading: isLoadingCrmLeadsState } = useQuery({
    queryKey: campaignsKeys.crmLeads(campaignId, sourceId, crmType),
    queryFn: () => getCrmLeadsStateRequest(workspaceId, crmType, sourceId),
    refetchInterval: ({ state: { data } }) =>
      data?.state === "in_progress" ? 3000 : false, // Poll every 3s if still processing
    refetchIntervalInBackground: true,
  });

  return {
    crmLeadsState,
    isLoadingCrmLeadsState,
  };
}
